
import { getRequest } from '../../../services/httpService';
import { useApiGet, useApiPost } from '../../../services/tanstackApiService';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import type { AdvanceConsumptionMetadata } from '../../../components/Billing/Invoice/PackageAdvanceCollection';

// ----------------------
// Types & Interfaces
// ----------------------


export interface InvoiceStatusCounts {
  billed: number;
  draft: number;
  dispensed: number;
  unsettled: number;
  unbilled: number;
  patient: number;
  donor: number;
  surrogate: number;
  cryo: number;
  company: number;
}


export interface InvoiceMeta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
  status_counts: InvoiceStatusCounts;
  category_count?: {
    patient?: number;
    donor?: number;
    surrogate?: number;
    cryo?: number;
    company?: number;
  };
  bill_type_count?: {
    total?: number;
    clinical?: number;
    pharmacy?: number;
    services?: number;
  };
}


export interface DelinkLog {
  id: number;
  action: string;
  recipient_patient_id: number | null;
  recipient_name: string | null;
  recipient_mrn: string | null;
  reason: string | null;
  created_on: string | null;
  created_by: string | null;
}

export interface InvoiceItem {
  id: number | null;
  type: string;
  appointment_id: number;
  clinicName: string;
  clinic?: string; // Added clinic field to match API response
  clinic_id?: number | null; // Clinic ID from API response (normalized to number)
  patient: string;
  patient_name: string | null;
  patient_mrn_no: string;
  gender: string;
  age: number | null;
  patient_id: number;
  patient_type: string;
  invoice_patient_type: string | null;
  visit_date: string; // ISO date
  visit_id: number;
  status: string;
  bill_number: string | null;
  bill_type: string | null;
  Provider: string | null;
  treatment_doctor_name?: string | null;
  due_date?: string | null;
  invoice_due_status?: string | null;
  total_amount: number | null;
  concession_amount: number | null;
  net_amount: number | null;
  paid_amount: number | null;
  balance_amount: number | null;
  remark: string | null;
  advance_consumption_metadata?: AdvanceConsumptionMetadata[] | null;
  payment_source?: 'donor' | 'recipient' | null;
  recipient_patient_id?: number | null;
  is_donor_tagged?: boolean;
  label?: string | null;
  label_mrn_no?: string | null;
  can_settle_invoice?: boolean;
  delink_log?: DelinkLog | null;
  payment_count?: number;
}


export interface InvoiceListResponse {
  meta: InvoiceMeta;
  objects: InvoiceItem[];
}




export interface InvoiceFilters {
  clinic?: number;
  patient_id?: number;
  invoice_patient_type?: 'patient' | 'donor' | 'surrogate' | 'company' | 'cryo';
  // NOTE: API currently uses 'PH' for Pharmacy; keep both for safety
  bill_type?: string;
  from_date?: string;     // YYYY-MM-DD
  to_date?: string;       // YYYY-MM-DD
  limit?: number;
  offset?: number;
  search_query?: string;  // universal search
  status?: string;        // e.g. 'billed', 'draft', etc.
}


// ----------------------
// Utilities
// ----------------------

/**
 * Converts InvoiceFilters to URLSearchParams for API requests.
 */
function filtersToParams(filters: InvoiceFilters): URLSearchParams {
  const {
    clinic,
    patient_id,
    invoice_patient_type,
    bill_type,
    from_date,
    to_date,
    limit = 10,
    offset = 0,
    search_query,
    status
  } = filters;

  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (clinic != null) params.set('clinic', String(clinic));
  if (patient_id != null) params.set('patient_id', String(patient_id));
  if (invoice_patient_type) params.set('invoice_patient_type', invoice_patient_type);
  if (bill_type) params.set('bill_type', bill_type);
  if (from_date) params.set('from_date', from_date);
  if (to_date) params.set('to_date', to_date);
  if (search_query) params.set('search_query', search_query);
  if (status) params.set('invoice_status_type', status);
  return params;
}

/**
 * Fetches invoices from API using provided filters.
 */
export function fetchInvoices(filters: InvoiceFilters): Promise<InvoiceListResponse> {
  const params = filtersToParams(filters);
  return getRequest(`invoices/?${params.toString()}`);
}


// ----------------------
// Clinical Patients (Invoice Module)
// ----------------------

export interface ClinicalInvoicePatient {
  id: number;
  appointment_date?: string;
  first_name: string;
  last_name?: string;
  medical_record_no?: string;
  gender__name_label?: string | null;
  mobile_number?: string;
  appointment_user?: Array<{ first_name: string; last_name?: string }>;
}

export interface ClinicalInvoicePatientsMeta {
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
}

export interface ClinicalInvoicePatientsResponse {
  meta: ClinicalInvoicePatientsMeta;
  objects: ClinicalInvoicePatient[];
}

export interface ClinicalInvoicePatientsFilters {
  clinic: number;
  from_date: string; // YYYY-MM-DD
  to_date: string;   // YYYY-MM-DD
  limit?: number;
  offset?: number;
  search?: string;
}

function clinicalPatientsFiltersToParams(filters: ClinicalInvoicePatientsFilters): URLSearchParams {
  const {
    clinic,
    from_date,
    to_date,
    limit = 10,
    offset = 0,
    search,
  } = filters;

  const params = new URLSearchParams({
    checked_in: 'true',
    patient_type: 'patient',
    from_date,
    to_date,
    limit: String(limit),
    offset: String(offset),
    clinic: String(clinic),
  });

  if (search) params.set('search', search);

  return params;
}

/**
 * Fetches clinical patients for invoice creation using the
 * /api/invoices/clinical-patients/ endpoint.
 *
 * Example URL:
 * /api/invoices/clinical-patients/?checked_in=true&patient_type=patient&from_date=2025-12-16&to_date=2025-12-16&limit=10&offset=0&clinic=7
 */
export function fetchClinicalInvoicePatients(
  filters: ClinicalInvoicePatientsFilters
): Promise<ClinicalInvoicePatientsResponse> {
  const params = clinicalPatientsFiltersToParams(filters);
  return getRequest(`invoices/clinical-patients/?${params.toString()}`);
}


/**
 * Builds a stable query key for React Query based on filters.
 */
export const buildInvoicesQueryKey = (filters: InvoiceFilters) => [
  'invoices',
  { ...filters }
];


// ----------------------
// Hooks
// ----------------------

export interface UseInvoicesQueryParams extends InvoiceFilters {
  enabled?: boolean;
}


/**
 * React Query hook to fetch invoices with filters.
 *
 * @example
 * const { data, isLoading, error } = useInvoicesQuery({
 *   clinic: 123,
 *   limit: 10,
 *   offset: 0,
 *   from_date: '2025-01-01',
 *   to_date: '2025-01-31',
 *   search_query: 'PCC-1505',
 *   enabled: !!clinicId
 * });
 * // data?.objects => array of invoices
 * // data?.meta.total_count => total count
 */
export function useInvoicesQuery(filters: UseInvoicesQueryParams) {
  const { enabled = true, ...rest } = filters;
  return useQuery<InvoiceListResponse>({
    queryKey: buildInvoicesQueryKey(rest),
    enabled,
    // Always treat data as stale so each "page" (offset) change triggers a fresh fetch
    staleTime: 0,
    gcTime: 300_000,
    // Ensure a refetch whenever the component using this hook mounts
    refetchOnMount: 'always',
    retry: 1,

    refetchOnWindowFocus: false,
    queryFn: () => fetchInvoices(rest),
  });
}


export interface UseInfiniteInvoicesParams extends Omit<InvoiceFilters, 'offset'> {
  enabled?: boolean;
  initialOffset?: number;
}


/**
 * Infinite pagination hook for invoices.
 *
 * @example
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage
 * } = useInfiniteInvoicesQuery({
 *   clinic: 123,
 *   limit: 20,
 *   from_date: '2025-01-01',
 *   to_date: '2025-02-01',
 *   search_query: '',
 *   enabled: true,
 *   initialOffset: 0
 * });
 *
 * const allInvoices = data?.pages.flatMap(p => p.objects) ?? [];
 *
 * <Button disabled={!hasNextPage || isFetchingNextPage} onClick={() => fetchNextPage()}>
 *   {isFetchingNextPage ? 'Loading...' : hasNextPage ? 'Load more' : 'No more'}
 * </Button>
 */
export function useInfiniteInvoicesQuery({
  enabled = true,
  initialOffset = 0,
  limit = 10,
  clinic,
  patient_id,
  invoice_patient_type,
  bill_type,
  from_date,
  to_date,
  search_query
}: UseInfiniteInvoicesParams) {
  return useInfiniteQuery<InvoiceListResponse, Error>({
    queryKey: buildInvoicesQueryKey({
      limit,
      offset: initialOffset,
      clinic,
      patient_id,
      invoice_patient_type,
      bill_type,
      from_date,
      to_date,
      search_query
    }),
    enabled,
    initialPageParam: initialOffset,
    staleTime: 30_000,
    gcTime: 300_000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: ({ pageParam }) =>
      fetchInvoices({
        limit,
        offset: pageParam as number,
        clinic,
        patient_id,
        invoice_patient_type,
        bill_type,
        from_date,
        to_date,
        search_query
      }),
    getNextPageParam: (lastPage) => {
      const nextUrl = lastPage.meta.next;
      if (!nextUrl) return undefined;
      try {
        const u = new URL(nextUrl);
        const offsetStr = u.searchParams.get('offset');
        if (!offsetStr) return undefined;
        const nextOffset = parseInt(offsetStr, 10);
        return Number.isNaN(nextOffset) ? undefined : nextOffset;
      } catch {
        return undefined;
      }
    }
  });
}


// ----------------------
// Invoice Payments API
// ----------------------

export interface PaymentDetail {
  payment_mode: string;
  paid_amount: number;
  currency: string;
  number: string;
}

export interface InvoicePayment {
  id: number;
  receipt_no: string;
  transaction_date: string;
  bill_amount: string;
  details: PaymentDetail[];
}

export interface InvoicePaymentsResponse {
  invoice_id: number;
  bill_number: string | null;
  total_payments: number;
  payments: InvoicePayment[];
}

/**
 * Fetches payments for a specific invoice.
 */
export function fetchInvoicePayments(invoiceId: number): Promise<InvoicePaymentsResponse> {
  return getRequest(`invoices/${invoiceId}/invoice_payments/`);
}

/**
 * Builds a stable query key for invoice payments.
 */
export const buildInvoicePaymentsQueryKey = (invoiceId: number) => [
  'invoice-payments',
  invoiceId
];

export interface UseInvoicePaymentsQueryParams {
  invoiceId: number;
  enabled?: boolean;
}

/**
 * React Query hook to fetch payments for a specific invoice.
 *
 * @example
 * const { data, isLoading, error } = useInvoicePaymentsQuery({
 *   invoiceId: 32,
 *   enabled: !!invoiceId
 * });
 * // data?.payments => array of payments
 * // data?.total_payments => total count
 */
export function useInvoicePaymentsQuery({
  invoiceId,
  enabled = true
}: UseInvoicePaymentsQueryParams) {
  return useQuery<InvoicePaymentsResponse>({
    queryKey: buildInvoicePaymentsQueryKey(invoiceId),
    enabled: enabled && invoiceId > 0,
    gcTime: 30_000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: () => fetchInvoicePayments(invoiceId),
  });
}


// ----------------------
// Receipt Details API
// ----------------------

export interface PaymentSummary {
  payment_mode_name: string;
  amount: number;
}

export interface PatientDetails {
  patient_name: string;
  mrn: string;
  age: number;
  gender: number;
  mobile_no: string;
}

export interface ClinicDetails {
  clinic_name: string;
  clinic_address: string;
  phone_no: string;
  email: string | null;
  website: string;
  logo: string | null;
}

export interface InvoiceDetailsForReceipt {
  bill_no: string | null;
}

export interface Receipt {
  id: number;
  payment_summary: PaymentSummary[];
  receipt_no: string;
  created_by: string;
  print_date_time: string;
  total_bill_amount: string;
  total_received_amount: number;
  patient_details: PatientDetails;
  clinic_details: ClinicDetails;
  invoice_details: InvoiceDetailsForReceipt;
}

export interface ReceiptResponse {
  success: boolean;
  receipt: Receipt;
}

/**
 * Fetches receipt details for printing.
 */
export function fetchReceiptDetails(paymentId: number): Promise<ReceiptResponse> {
  return getRequest(`payments/${paymentId}/receipt`);
}

/**
 * Builds a stable query key for receipt details.
 */
export const buildReceiptQueryKey = (paymentId: number) => [
  'receipt-details',
  paymentId
];

export interface UseReceiptQueryParams {
  paymentId: number;
  enabled?: boolean;
}

/**
 * React Query hook to fetch receipt details for printing.
 *
 * @example
 * const { data, isLoading, error } = useReceiptQuery({
 *   paymentId: 26,
 *   enabled: !!paymentId
 * });
 * // data?.receipt => receipt details for printing
 */
export function useReceiptQuery({
  paymentId,
  enabled = true
}: UseReceiptQueryParams) {
  return useQuery<ReceiptResponse>({
    queryKey: buildReceiptQueryKey(paymentId),
    enabled: enabled && paymentId > 0,
    gcTime: 30_000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: () => fetchReceiptDetails(paymentId),
  });
}


// ----------------------
// Refund Types & Functions
// ----------------------

export interface RefundItem {
  invoice_item_id: number;
  refund_reason: string;
  is_refunded?: boolean;
}

export interface InvoiceRefundPayload {
  remark: string;
  items: RefundItem[];
}

export interface InvoiceRefundResponse {
  success: boolean;
  message?: string;
  refund_id?: number;
}

/**
 * Helper to build the API parameters for creating a refund.
 * 
 * @param body - The refund payload containing remark and items
 * @returns Object with path and body for the API call
 * 
 * @example
 * const params = buildInvoiceRefundParams({
 *   remark: "Customer requested refund",
 *   items: [
 *     { invoice_item_id: 37, refund_reason: "Service cancelled" },
 *     { invoice_item_id: 38, refund_reason: "Duplicate billing" }
 *   ]
 * });
 */
export function buildInvoiceRefundParams(body: InvoiceRefundPayload) {
  return {
    path: 'invoices/invoice-refund/',
    body,
  };
}

/**
 * Hook to POST and create an invoice refund.
 * 
 * @example
 * const { mutate, mutateAsync, isPending } = useCreateInvoiceRefund();
 * 
 * // Using mutateAsync
 * const response = await mutateAsync(buildInvoiceRefundParams({
 *   remark: "Customer requested refund",
 *   items: [
 *     { invoice_item_id: 37, refund_reason: "Service cancelled" }
 *   ]
 * }));
 */
export function useCreateInvoiceRefund() {
  return useApiPost<InvoiceRefundResponse, InvoiceRefundPayload>();
}

// ----------------------
// Patient Drugs (By Pharmacy tab)
// ----------------------

export interface PatientDrug {
  id: number;
  drug: {
    id: number;
    name: string;
    brand_name?: string;
    strength?: string;
    stock_uom_code: string;
    route_code: string;
    molecule: string;
    molecule_label?: string;
    name_label: string;
    strength_type: string;
    strength_type_label: string;
    base_uom_code: string;
    base_uom_label: string;
  };
  frequency: string;
  instructions?: string | null;
  instructions_label?: string | null;
  no_of_days: number;
  quantity: number;
  remaining_quantity?: number;
  notes?: string | null;
  user?: number | null;
  user_full_name: string;
  patient?: number | null;
  drug_route?: string | null;
  drug_route_code: string;
  drug_route_code_label: string | null;
  default_drug_route_code: string;
  created_on: string | null;
  drug_frequency?: string | null;
  drug_instructions?: string;
}

export interface PatientDrugsResponse {
  meta: {
    limit: number;
    next: string | null;
    offset: number;
    previous: string | null;
    total_count: number;
  };
  objects: PatientDrug[];
}

/**
 * Fetch patient drugs for "By Pharmacy" tab
 * GET /patient-drugs/?patient={patientId}
 */
export function usePatientDrugs(patientId?: string | number) {
  return useApiGet<PatientDrugsResponse>(
    ['patient-drugs', patientId],
    {
      path: 'patient-drugs/',
      query: { patient: patientId },
    },
    {
      enabled: !!patientId,
    }
  );
}